import { ReactComponent as CI_DEVTECH } from '@/assets/ci/devtech.svg';
import { ReactComponent as OutlinkIcon } from '@/assets/images/icons/icn-outlink.svg';

import Divider from '@/components/atoms/Divider';
import ExternalLink from '@/components/atoms/ExternalLink';
import { LINK_CAREERS, LINK_DEVTECH_YOUTUBE, LINK_GITHUB } from '@/constants/links';
import { Alternate, HTML_LANG, listPath, useLang, useT } from '@/i18n';
import { GlobalIcon } from '@devsisters/wds-icons';
import { cn, MergeProps } from '@devsisters/wds-react';
import { Link, PageProps } from 'gatsby';
import React from 'react';
import { useIntersectionObserver } from 'usehooks-ts';
import GNBDialog from './GNBDialog';

type NavProps = React.ComponentPropsWithoutRef<'nav'>;
type BaseProps = Pick<PageProps, 'location'> & { languageSwitch: Alternate };

type Props = MergeProps<NavProps, BaseProps>;
function TopNavigation(props: Props) {
  const { location, languageSwitch, className, ...navProps } = props;
  const { isIntersecting, ref } = useIntersectionObserver({ threshold: 0.5, initialIsIntersecting: true });

  const lang = useLang();
  const t = useT();
  const isRootPage = location.pathname === listPath(lang);
  return (
    <>
      <div ref={ref} className={'absolute left-0 top-0 h-[1px] w-[1px]'} />
      <nav
        className={cn(
          'sticky top-0 z-50 flex h-[var(--app-top-nav-height,)] w-full items-center bg-white',
          'transition-colors duration-300',
          isRootPage && 'fixed data-[top]:border-white/10 data-[top]:bg-transparent data-[top]:text-white',
          className,
        )}
        {...(isIntersecting && { 'data-top': true })}
        {...navProps}
      >
        <div className={cn('container-layout mx-auto flex items-center justify-between', 'pl-7 pr-3 lg:px-8')}>
          <Link className={'gtm-gnb-logo'} to={listPath(lang)}>
            <CI_DEVTECH className={'h-7 lg:h-8'} height={20} />
          </Link>

          {/* Mobile: Menu */}
          <div className={'flex items-center lg:hidden'}>
            <Link
              className={'gtm-gnb-language-switch flex h-13 items-center gap-2 px-3 text-body-14 font-bold'}
              to={languageSwitch.path}
              hrefLang={HTML_LANG[languageSwitch.lang]}
              aria-label={t('nav.switchLang')}
            >
              <GlobalIcon size={'small'} />
              <span>{t('nav.switchLangShort')}</span>
            </Link>
            <GNBDialog languageSwitch={languageSwitch} />
          </div>

          {/* PC: Menu */}
          <div className={'hidden items-center gap-9 font-bold lg:flex'}>
            <Link className={'gtm-gnb-links-tech text-primary underline'} to={listPath(lang)}>
              {t('nav.blog')}
            </Link>
            <Divider className={'h-6'} direction={'vertical'} />
            <ExternalLink className={'gtm-gnb-links-github'} href={LINK_GITHUB}>
              <span>{'GitHub'}</span>
              <OutlinkIcon width={16} />
            </ExternalLink>
            <Divider className={'h-6'} direction={'vertical'} />
            <ExternalLink className={'gtm-gnb-links-devtech-youtube'} href={LINK_DEVTECH_YOUTUBE}>
              <span>{'YouTube'}</span>
              <OutlinkIcon width={16} />
            </ExternalLink>
            <Divider className={'h-6'} direction={'vertical'} />
            <ExternalLink className={'gtm-gnb-links-careers'} href={LINK_CAREERS}>
              <span>{t('nav.careers')}</span>
              <OutlinkIcon width={16} />
            </ExternalLink>
            <Divider className={'h-6'} direction={'vertical'} />
            <Link
              className={'gtm-gnb-language-switch flex items-center gap-2 hover:text-primary'}
              to={languageSwitch.path}
              hrefLang={HTML_LANG[languageSwitch.lang]}
            >
              <GlobalIcon size={'small'} />
              <span>{t('nav.switchLang')}</span>
            </Link>
          </div>
        </div>
      </nav>
    </>
  );
}

export default TopNavigation;
